<script setup lang="ts">
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, ref, watch } from 'vue';
  import Dropdown from '../Dropdown/Dropdown.vue';
  import { DropdownPlacement } from '../Dropdown/Dropdown.types';
  import DropdownItem from '../Dropdown/DropdownItem/DropdownItem.vue';
  import FontIcon from '../FontIcon/FontIcon.vue';
  import { dropdownItemWideGapClass } from '../Dropdown/DropdownItem/DropdownItem.css';
  import { caretClass, triggerClass } from './IconDropdown.css';
  import type {
    IconDropdownEmits,
    IconDropdownProps,
  } from './IconDropdown.types';

  const props = withDefaults(defineProps<IconDropdownProps>(), {
    modelValue: null,
    disabled: false,
    disableTeleport: false,
    placement: DropdownPlacement.BOTTOM_START,
  });

  const emit = defineEmits<IconDropdownEmits>();

  const internalValue = ref<string | null>(props.modelValue);

  watch(
    () => props.modelValue,
    (value) => {
      internalValue.value = value;
    },
  );

  const selectedOption = computed(() =>
    props.options.find((option) => option.value === internalValue.value),
  );

  const triggerLabel = computed(
    () => props.ariaLabel ?? selectedOption.value?.label,
  );

  const onSelect = (value: string) => {
    internalValue.value = value;
    emit('update:modelValue', value);
  };
</script>

<template>
  <Dropdown
    :model-value="modelValue"
    :disabled="disabled"
    :disable-teleport="disableTeleport"
    :placement="placement"
    :test-id="testId"
    :id="id"
    trigger-as-child
    @update:model-value="onSelect"
  >
    <template #trigger="{ isOpen }">
      <button
        type="button"
        :class="triggerClass"
        :disabled="disabled"
        :aria-label="triggerLabel"
        :data-test-id="testId"
      >
        <FontIcon v-if="selectedOption" :icon-name="selectedOption.icon" />
        <FontIcon
          :icon-name="TUNDR_ICONS.CHEVRON_DOWN"
          :class="caretClass"
          :rotation="isOpen ? 180 : 0"
        />
      </button>
    </template>

    <DropdownItem
      v-for="option in options"
      :key="option.value"
      :class="dropdownItemWideGapClass"
      :value="option.value"
      :label="option.label"
      :icon="option.icon"
      :test-id="testId ? `${testId}-option-${option.value}` : undefined"
    />
  </Dropdown>
</template>
